// Styling tokens lifted from the Inkfree Dashboard reference. Kept in one
// place — and scoped to the dashboard — so the screens below never carry a raw
// hex, mirroring how the auth screens use `AuthReferenceStyles`.
import strings from "global/constants/StringConstants";
import { button, contentCard, signer } from "theme/colors";

export const dashRef = {
  heading: signer.ink, // greeting + card headings
  pageSubText: signer.text.subtitle, // greeting sub-line (WCAG 1.4.3, was #8A85A0 at 3.53:1)
  metaText: signer.text.faint, // card description, row meta, progress label
  rowTitle: signer.ink,
  rowDivider: signer.surface.muted, // hairline between activity rows
  rowHover: "#FAF9F6",
  progressTrack: signer.border.light,
  progressFill: signer.orange.main,
  // Stat cards + dropzone share the card hairline and shadow.
  cardBorder: contentCard.border,
  cardShadow: "0 1px 3px rgba(33,21,71,0.05)",
  // Dropzone colours now live in `CustomDropzone.tokens` — the create-request
  // step shares the same surface.
  // Range picker on the renewals card.
  filterBorder: button.secondaryBorderWarm,
  filterBorderHover: signer.ink,
  filterText: signer.text.secondary,
  // Phone-only "needs your signature" callout (mobile reference).
  calloutBorder: "#F5C9AF",
  calloutBorderHover: signer.orange.main,
  chevron: "#C9C5D6",
  // Higher-contrast step for the callout's 11px sub-line TEXT — `accent` is
  // now the app brand red (already ≥4.5:1 on white), but this keeps the extra
  // step for a wider WCAG 2.1 AA margin. Same shade the dropzone uses for
  // "browse files".
  accentText: signer.orange.dark,
};

// Geometry that steps down on the smaller viewports. Same breakpoint bands as
// `dashFont`; the md value is always the original desktop figure.
export const dashSize = {
  // Gap between the dashboard's top-level blocks.
  sectionGap: { xs: 2.25, sm: 2.5, md: 4 },
  // Stat card: tinted icon tile, its glyph, and the card's own padding.
  statTile: { xs: 34, sm: 46, md: 48 },
  statTileRadius: { xs: "10px", sm: "13px", md: "14px" },
  statIcon: { xs: 17, sm: 21, md: 22 },
  statCardPadding: { xs: "14px", sm: "18px", md: "20px 20px 18px" },
  statCardRadius: { xs: "15px", sm: "16px" },
  // Label and sub-label inside a stat card. The reference leaves them at the
  // browser default (~1.2); the theme's 1.5 body line-height adds ~5px a line,
  // which compounds down the two-up grid. Desktop keeps the theme figure.
  statTextLineHeight: { xs: 1.25, md: 1.5 },
  // Section cards (Recent activity / Monthly renewals).
  sectionCardPadding: { xs: 1.875, sm: 2.5, md: 2.5 },
};

// Accent + icon-tile fill per stat card, in reference order. `accent` colors
// the icon (3:1 is enough for a graphic); `text` colors the small sub-label,
// which needs 4.5:1 (WCAG 1.4.3), so the red and green get darker text shades.
export const statAccents = {
  actionRequired: { accent: signer.orange.main, text: signer.orange.main, tint: "#FDF0E9" },
  waitingForOthers: { accent: signer.purple.main, text: signer.purple.main, tint: "#F0EBF8" },
  expiringSoon: { accent: "#EF403A", text: signer.danger.main, tint: "#FDECEB" },
  completed: { accent: "#1E9E6A", text: "#18733F", tint: "#E7F6EF" },
};

// Type scale from the reference (px → rem at a 16px root).
//
// Each entry is a breakpoint map so the same token carries the phone, tablet
// and desktop sizes the reference specifies:
//   xs  (<600px)   phone  — "Dashboard - Mobile & Tablet" 375/390 columns
//   sm  (600-899)  tablet — the same file's 834 column
//   md+ (>=900)    desktop — unchanged from the original `Dashboard` reference
// Anything the three viewports share is left as a plain string.
export const dashFont = {
  pageHeading: { xs: "1.25rem", sm: "1.5rem", md: "1.625rem" }, // 20 / 24 / 26
  pageSub: { xs: "0.78125rem", sm: "0.8125rem", md: "0.84375rem" }, // 12.5 / 13 / 13.5
  cardHeading: { xs: "0.90625rem", sm: "1rem" }, // 14.5 / 16  card titles
  cardDescription: { xs: "0.6875rem", sm: "0.75rem" }, // 11 / 12  card sub-line
  rowTitle: { xs: "0.8125rem", sm: "0.84375rem" }, // 13 / 13.5  activity row title
  rowMeta: { xs: "0.71875rem", sm: "0.75rem" }, // 11.5 / 12  Created by … · date
  statusPill: { xs: "0.625rem", sm: "0.65625rem", md: "0.6875rem" }, // 10 / 10.5 / 11
  progressLabel: "0.6875rem", // 11px  1/3
  link: { xs: "0.78125rem", sm: "0.8125rem" }, // 12.5 / 13  View all
  filter: { xs: "0.6875rem", sm: "0.78125rem" }, // 11 / 12.5  6 months
  statValue: { xs: "1.5rem", sm: "1.625rem", md: "1.75rem" }, // 24 / 26 / 28
  statLabel: { xs: "0.78125rem", sm: "0.8125rem" }, // 12.5 / 13  Action required
  statSub: { xs: "0.6875rem", sm: "0.71875rem" }, // 11 / 11.5  Needs your signature
};

// Status pill colours. The reference shows four families; every request status
// the API can return is mapped onto one of them, with a neutral fallback.
// Pill text is 10-11px, so each color clears 4.5:1 on its own fill (WCAG
// 1.4.3); the reference's #1E9E6A / #B8860B / #EF403A did not (3.0-3.4:1).
const statusPalette = {
  success: { color: "#18733F", bg: "#E7F6EF" },
  pending: { color: signer.warn.text, bg: "#FBF3DC" },
  progress: { color: signer.purple.main, bg: "#F0EBF8" },
  danger: { color: signer.danger.main, bg: "#FDECEB" },
  neutral: { color: signer.text.meta, bg: "#F1F0F5" },
};

export const getActivityStatusStyle = (status: string) => {
  switch (status) {
    case strings.signed:
    case strings.completed:
    case strings.approved:
      return statusPalette.success;
    case strings.pending:
    case strings.scheduled:
      return statusPalette.pending;
    case strings.inReview:
    case strings.draft:
      return statusPalette.progress;
    case strings.expired:
    case strings.declined:
    case strings.rejected:
    case strings.voided:
      return statusPalette.danger;
    default:
      return statusPalette.neutral;
  }
};
